import React, { useContext, useEffect, useState } from "react";
import { Button, Card, Form, Input, Layout, Modal, Spin, Typography, message } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../Constants";
import AppContext from "../../AppContext";

const { Text } = Typography;
const { Content } = Layout;

// Self-service profile for Individual Event/Wedding (virtual company) accounts. These users are
// not companies, so only personal details and password are exposed here — no company fields.
export default function MyProfilePage() {
  const { t } = useTranslation();
  const authCtx = useContext(AppContext);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/User/me`);
      setProfileData(response.data);
      form.setFieldsValue({
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        email: response.data.email,
      });
    } catch (err) {
      console.error("Failed to load profile", err);
      message.error(t("profilePage.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      await axios.put(`${BASE_URL}/User/me`, values);
      setProfileData((prev) => ({ ...prev, ...values }));
      // The header reads the name from the stored login profile, so refresh it too.
      authCtx.login({ ...authCtx.profile, ...values });
      message.success(t("profilePage.saveSuccess"));
    } catch (err) {
      console.error("Failed to update profile", err);
      message.error(t("profilePage.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleSendResetLink = async () => {
    setSendingReset(true);
    try {
      await axios.post(
        `${BASE_URL}/Auth/ResetPassword?username=${encodeURIComponent(profileData.username)}`
      );
      Modal.success({
        title: t("messages.success.passwordReset"),
        content: t("profilePage.resetLinkSent", { email: profileData.email }),
      });
    } catch (err) {
      console.error("Failed to send password reset link", err);
      message.error(t("messages.error.passwordResetFailed"));
    } finally {
      setSendingReset(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Layout className="min-h-full bg-transparent">
      <header className="py-4 bg-transparent">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight text-white">
            {t("userMenu.yourProfile")}
          </h1>
        </div>
      </header>
      <Content className="rounded-lg px-5 py-6 shadow bg-white sm:px-6">
        <Card
          title={
            <span>
              <UserOutlined /> {t("profilePage.personalDetails")}
            </span>
          }
          style={{ marginBottom: 24, maxWidth: 640 }}
        >
          <Form form={form} layout="vertical" onFinish={handleSave} disabled={!profileData}>
            <Form.Item label={t("profilePage.username")}>
              <Input value={profileData?.username} disabled />
            </Form.Item>
            <Form.Item
              name="firstName"
              label={t("profilePage.firstName")}
              rules={[{ required: true, whitespace: true, message: t("profilePage.required") }]}
            >
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item
              name="lastName"
              label={t("profilePage.lastName")}
              rules={[{ required: true, whitespace: true, message: t("profilePage.required") }]}
            >
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item
              name="email"
              label={t("profilePage.email")}
              rules={[
                { required: true, message: t("profilePage.required") },
                { type: "email", message: t("profilePage.invalidEmail") },
              ]}
            >
              <Input maxLength={256} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>
              {t("profilePage.save")}
            </Button>
          </Form>
        </Card>

        <Card
          title={
            <span>
              <LockOutlined /> {t("profilePage.password")}
            </span>
          }
          style={{ maxWidth: 640 }}
        >
          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            {t("profilePage.passwordHelp")}
          </Text>
          <Button onClick={handleSendResetLink} loading={sendingReset} disabled={!profileData}>
            {t("profilePage.sendResetLink")}
          </Button>
        </Card>
      </Content>
    </Layout>
  );
}
